import { Button } from '@consta/uikit/Button'
import { cnMixFlex } from '@consta/uikit/MixFlex'
import { cnMixSpace } from '@consta/uikit/MixSpace'
import { Modal } from '@consta/uikit/Modal'
import classnames from 'classnames'
import { VALID_RESULT } from 'common/constants'
import type { ValidationResult } from 'common/types'
import type { FormEventHandler, ReactNode } from 'react'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import type { UseBooleanFlagApi } from '../../../hooks'
import { useBooleanFlag } from '../../../hooks'
import css from './DialogModal.css'

const container = classnames(css.dialog, cnMixSpace({ p: 's' }))

const form = cnMixFlex({ flex: 'flex', direction: 'column', justify: 'space-between', gap: 'xs' })

const controls = classnames(cnMixFlex({ flex: 'inline-flex', gap: '2xs' }))

type CallbackOption = {
  performAction: boolean
  value: unknown
}

export type OnEndCallback = (option: CallbackOption) => Promise<void> | void

type DialogModalProps = {
  className?: string
  submitLabel: string
  cancelLabel: string
  initialValue: unknown
  validateFields?: (value: unknown) => ValidationResult
  children: (value: unknown, setValue: (value: unknown) => void) => ReactNode
  onEnd: OnEndCallback
  submitButtonProps?: Record<string, unknown>
}

export const DialogModal = forwardRef<UseBooleanFlagApi, DialogModalProps>(function DeleteTieModal(
  {
    validateFields = () => VALID_RESULT,
    className = '',
    initialValue,
    cancelLabel,
    submitLabel,
    onEnd,
    children,
    submitButtonProps,
  },
  ref,
) {
  const [value, setValue] = useState<unknown>(initialValue)

  const [isModalOpen, api] = useBooleanFlag(false)

  useImperativeHandle(ref, () => api, [api])

  const closeModal = () => {
    onEnd({ performAction: false, value })
    api.setFalse()
  }

  const handleForm: FormEventHandler = e => {
    e.preventDefault()
    switch (e.type) {
      case 'reset': {
        onEnd({ performAction: false, value })
        break
      }
      case 'submit': {
        onEnd({ performAction: true, value })
        break
      }
      default: {
        console.warn(`Необработанный тип события ${e.type}`)
      }
    }

    closeModal()
  }

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const disabledAction = !validateFields(value).isValid

  return (
    <Modal
      className={classnames(container, className)}
      isOpen={isModalOpen}
      hasOverlay={true}
      onEsc={closeModal}
      onClickOutside={closeModal}>
      <form className={form} onSubmit={handleForm} onReset={handleForm}>
        {children(value, setValue)}
        <span className={controls}>
          <Button
            className={css.button}
            label={submitLabel}
            disabled={disabledAction}
            view="primary"
            size="xs"
            type="submit"
            {...submitButtonProps}
          />
          <Button className={css.button} label={cancelLabel} view="ghost" size="xs" type="reset" />
        </span>
      </form>
    </Modal>
  )
})
